
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { CardFormValues } from '@/components/auth/CardVerificationForm';
import { UserFormValues } from '@/components/auth/UserInfoForm';
import { useAuthSubmit } from '../auth/useAuthSubmit';
import { usePaymentSubmit } from '../payment/usePaymentSubmit';
import { supabase } from '@/integrations/supabase/client';
import { AuthFormValues } from '@/types/auth';
import { CardDetails } from '@/types/payment';

export const useSignupSubmit = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { handleSignUp, handleSignIn, handleAuthSignup, handleAuthSignin } = useAuthSubmit();
  const { handlePaymentSetup } = usePaymentSubmit();

  const checkUserExists = async (email: string) => {
    try {
      // Check if user exists in auth system
      const { data, error } = await supabase.auth.signInWithOtp({
        email: email,
        options: {
          shouldCreateUser: false,
        }
      });
      
      // If we get here without error, the user likely exists
      // But we'll make extra sure by checking if we got data
      const exists = !error && !!data;
      
      return { exists, email };
    } catch (error) {
      console.error('Error checking if user exists:', error);
      return { exists: false, email };
    }
  };
  
  const signInUser = async (email: string, password: string) => {
    try {
      await handleAuthSignin(email, password);
      toast({
        title: "Welcome back",
        description: "Successfully signed in.",
      });
      return true;
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to sign in. Please try again.",
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleSignup = async (
    userData: UserFormValues,
    cardData: CardDetails,
    verificationScore?: number,
    bitcoinAddress?: string
  ) => {
    try {
      // Convert UserFormValues to AuthFormValues before passing to handleAuthSignup
      const authFormData: AuthFormValues = {
        email: userData.email,
        password: userData.password,
        fullName: userData.username, // Map username to fullName as expected by AuthFormValues
      };
      
      // The handleAuthSignup now returns information about whether the user exists
      const result = await handleAuthSignup(authFormData);

      // Check if this is an existing user
      if (result && result.exists) {
        // We don't need to create the user, but still need to handle the card
        // If they successfully sign in in the next step, we'll still process their card
        return result;
      }

      if (result && !result.exists && result.id) {
        // Setup payment method - card or bitcoin
        await handlePaymentSetup(
          result.id, 
          cardData, 
          verificationScore,
          bitcoinAddress
        );

        toast({
          title: "Account created",
          description: bitcoinAddress 
            ? "Your account has been created with Bitcoin verification." 
            : "Your account has been created with AI-verified payment credentials.",
        });
        
        // Redirect to transfer page instead of email verification
        navigate('/pin-transfer');
      }
      
      return result;
    } catch (error: any) {
      console.error('Signup error:', error);
      toast({
        title: "Error",
        description: error.message || "There was a problem creating your account.",
        variant: "destructive",
      });
      throw error;
    }
  };

  return { 
    handleSignup,
    checkUserExists,
    signInUser
  };
};
